import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Plus, Trash2, Edit, Sparkles, GitBranch, Box, Activity, FileText,
  Link2, Download, Loader2, CheckCircle2, XCircle, AlertTriangle,
  ArrowRight, BookOpen, Network, History, Brain, X, FolderTree, Layers, Search, FileDown, Clock
} from 'lucide-react';

import ClassFormDialog from '@/components/ontology/ClassFormDialog';
import RelationFormDialog from '@/components/ontology/RelationFormDialog';
import IndividualFormDialog from '@/components/ontology/IndividualFormDialog';
import TripleFormDialog from '@/components/ontology/TripleFormDialog';
import ClassTreeNode from '@/components/ontology/ClassTreeNode';
import RecentActivityList from '@/components/ontology/RecentActivityList';
import ClassDetail from '@/components/ontology/ClassDetail';
import IndividualDetailView from '@/components/ontology/IndividualDetailView';
import { API, kindBadgeClass, healthColor, MarkdownBrief } from '@/components/ontology/OntologyHelpers';
import GraphTab from '@/components/ontology/tabs/GraphTab';
import SemanticSearchTab from '@/components/ontology/tabs/SemanticSearchTab';
import DiffTab from '@/components/ontology/tabs/DiffTab';

const OntologyPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [classTree, setClassTree] = useState({ roots: [], total: 0 });
  const [relations, setRelations] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [health, setHealth] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [changeLog, setChangeLog] = useState([]);
  const [classFilter, setClassFilter] = useState('');
  const [individualSearch, setIndividualSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  const [classDialog, setClassDialog] = useState(null);
  const [relationDialog, setRelationDialog] = useState(null);
  const [individualDialog, setIndividualDialog] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedIndividual, setSelectedIndividual] = useState(null);
  const [individualDetail, setIndividualDetail] = useState(null);
  const [tripleDialog, setTripleDialog] = useState(null);

  const [aiExtractOpen, setAiExtractOpen] = useState(false);
  const [aiExtracting, setAiExtracting] = useState(false);
  const [availableNotes, setAvailableNotes] = useState([]);
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);

  // Semantic search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchRunning, setSearchRunning] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  // Knowledge Brief
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefData, setBriefData] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);

  // Audit Alert
  const [auditConfig, setAuditConfig] = useState(null);
  const [auditSaving, setAuditSaving] = useState(false);
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  // Diff Viewer
  const nowIso = new Date().toISOString().slice(0, 16);
  const weekAgoIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
  const [diffFrom, setDiffFrom] = useState(weekAgoIso);
  const [diffTo, setDiffTo] = useState(nowIso);
  const [diffClassId, setDiffClassId] = useState('');
  const [diffRunning, setDiffRunning] = useState(false);
  const [diffResult, setDiffResult] = useState(null);

  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphIncludeIndivs, setGraphIncludeIndivs] = useState(true);

  const fetchClasses = useCallback(async () => {
    try {
      const [listRes, treeRes] = await Promise.all([
        axios.get(`${API}/ontology/classes`, { headers }),
        axios.get(`${API}/ontology/classes/tree`, { headers }),
      ]);
      setClasses(listRes.data || []);
      setClassTree(treeRes.data || { roots: [], total: 0 });
    } catch { /* */ }
  }, [headers]);

  const fetchRelations = useCallback(async () => {
    try { const r = await axios.get(`${API}/ontology/relations`, { headers }); setRelations(r.data || []); } catch { /* */ }
  }, [headers]);

  const fetchIndividuals = useCallback(async () => {
    try {
      const params = {};
      if (classFilter) params.class_id = classFilter;
      if (individualSearch) params.search = individualSearch;
      const r = await axios.get(`${API}/ontology/individuals`, { params, headers });
      setIndividuals(r.data || []);
    } catch { /* */ }
  }, [headers, classFilter, individualSearch]);

  const fetchHealth = useCallback(async () => {
    try { const r = await axios.get(`${API}/ontology/health`, { headers }); setHealth(r.data); } catch { /* */ }
  }, [headers]);

  const fetchSuggestions = useCallback(async () => {
    try { const r = await axios.get(`${API}/ontology/suggestions?status=pending`, { headers }); setSuggestions(r.data || []); } catch { /* */ }
  }, [headers]);

  const fetchChangeLog = useCallback(async () => {
    try { const r = await axios.get(`${API}/ontology/change-log?limit=100`, { headers }); setChangeLog(r.data || []); } catch { /* */ }
  }, [headers]);

  const fetchNotesForAI = useCallback(async () => {
    try { const r = await axios.get(`${API}/pkm/notes?limit=200`, { headers }); setAvailableNotes(r.data || []); } catch { /* */ }
  }, [headers]);

  const fetchAuditConfig = useCallback(async () => {
    try { const r = await axios.get(`${API}/ontology/audit-alert/config`, { headers }); setAuditConfig(r.data); } catch { /* */ }
  }, [headers]);

  const fetchGraph = useCallback(async () => {
    setGraphLoading(true);
    try {
      const r = await axios.get(`${API}/ontology/graph?include_individuals=${graphIncludeIndivs}`, { headers });
      const rawNodes = r.data.nodes || [];
      const rawEdges = r.data.edges || [];
      const nodes = rawNodes.map((n, idx) => {
        const angle = (idx * 2 * Math.PI) / Math.max(rawNodes.length, 1);
        const radius = n.type === 'class' ? 260 : 440;
        const isClass = n.type === 'class';
        return {
          id: n.id,
          position: { x: 500 + Math.cos(angle) * radius, y: 300 + Math.sin(angle) * radius },
          data: { label: n.label },
          style: {
            background: isClass ? (n.color || '#002FA7') : '#FFFFFF',
            color: isClass ? '#FFFFFF' : '#111827',
            border: isClass ? 'none' : '1.5px solid #9CA3AF',
            borderRadius: isClass ? 10 : 999,
            fontSize: 11,
            padding: 8,
            width: isClass ? 140 : 120,
          },
        };
      });
      const edgeColor = { hierarchy: '#002FA7', relation: '#AF52DE', instance: '#9CA3AF', triple: '#34C759' };
      const edges = rawEdges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: e.label,
        animated: e.type === 'hierarchy',
        style: { stroke: edgeColor[e.type] || '#6B7280', strokeWidth: e.type === 'hierarchy' ? 2 : 1.3 },
        labelStyle: { fontSize: 10, fill: '#4B5563' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#9CA3AF' },
      }));
      setGraphData({ nodes, edges });
    } finally { setGraphLoading(false); }
  }, [headers, graphIncludeIndivs]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchClasses(), fetchRelations(), fetchHealth(), fetchSuggestions(), fetchAuditConfig()]).finally(() => setLoading(false));
  }, [fetchClasses, fetchRelations, fetchHealth, fetchSuggestions, fetchAuditConfig]);

  useEffect(() => { if (tab === 'individuals') fetchIndividuals(); }, [tab, fetchIndividuals]);
  useEffect(() => { if (tab === 'audit') fetchChangeLog(); }, [tab, fetchChangeLog]);
  useEffect(() => { if (tab === 'graph') fetchGraph(); }, [tab, fetchGraph]);

  // Deep-link support: /ontology?focus=<individual_id> — jump straight to the
  // individual's detail panel. Used by CoP Stage 3 approval toast to show the
  // steward where their just-created knowledge asset fits in the ontology.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId || !token) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/ontology/individuals/${focusId}`, { headers });
        setTab('individuals');
        setSelectedIndividual(r.data);
        setIndividualDetail(r.data);
        toast.info(`Focused on "${r.data.name}"`, { duration: 3000 });
        // Clean up the query param so a page refresh doesn't re-fire
        searchParams.delete('focus');
        setSearchParams(searchParams, { replace: true });
      } catch {
        toast.error('Could not find that individual');
      }
    })();
    /* eslint-disable-next-line */
  }, [searchParams.get('focus'), token]);

  const submitClass = async (form) => {
    try {
      if (classDialog.mode === 'edit') {
        await axios.patch(`${API}/ontology/classes/${form.class_id}`, form, { headers });
        toast.success('Class updated');
      } else {
        await axios.post(`${API}/ontology/classes`, form, { headers });
        toast.success('Class created');
      }
      setClassDialog(null); fetchClasses(); fetchHealth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteClass = async (node) => {
    if (!window.confirm(`Delete class "${node.name}"?`)) return;
    try {
      await axios.delete(`${API}/ontology/classes/${node.class_id}`, { headers });
      toast.success('Class deleted');
      fetchClasses(); fetchHealth();
      if (selectedClass?.class_id === node.class_id) setSelectedClass(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const submitRelation = async (form) => {
    try {
      if (relationDialog.mode === 'edit') {
        await axios.patch(`${API}/ontology/relations/${form.relation_id}`, form, { headers });
        toast.success('Relation updated');
      } else {
        await axios.post(`${API}/ontology/relations`, form, { headers });
        toast.success('Relation created');
      }
      setRelationDialog(null); fetchRelations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteRelation = async (rel) => {
    if (!window.confirm(`Delete relation "${rel.name}"?`)) return;
    try {
      const r = await axios.delete(`${API}/ontology/relations/${rel.relation_id}`, { headers });
      toast.success(`Relation deleted (${r.data?.triples_removed || 0} triples removed)`);
      fetchRelations(); fetchHealth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const submitIndividual = async (form) => {
    try {
      if (individualDialog.mode === 'edit') {
        await axios.patch(`${API}/ontology/individuals/${form.individual_id}`, form, { headers });
        toast.success('Individual updated');
      } else {
        await axios.post(`${API}/ontology/individuals`, form, { headers });
        toast.success('Individual created');
      }
      setIndividualDialog(null); fetchIndividuals(); fetchHealth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const openIndividualDetail = async (ind) => {
    setSelectedIndividual(ind);
    try { const r = await axios.get(`${API}/ontology/individuals/${ind.individual_id}`, { headers }); setIndividualDetail(r.data); } catch { /* */ }
  };

  const deleteIndividual = async (ind) => {
    if (!window.confirm(`Delete "${ind.name}"?`)) return;
    try {
      await axios.delete(`${API}/ontology/individuals/${ind.individual_id}`, { headers });
      toast.success('Individual deleted');
      setSelectedIndividual(null); setIndividualDetail(null);
      fetchIndividuals(); fetchHealth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const submitTriple = async (form) => {
    try {
      await axios.post(`${API}/ontology/triples`, form, { headers });
      toast.success('Triple added');
      setTripleDialog(null);
      if (selectedIndividual) openIndividualDetail(selectedIndividual);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteTriple = async (triple_id) => {
    try {
      await axios.delete(`${API}/ontology/triples/${triple_id}`, { headers });
      if (selectedIndividual) openIndividualDetail(selectedIndividual);
    } catch { toast.error('Delete failed'); }
  };

  const runAIExtract = async () => {
    if (selectedNoteIds.length === 0) { toast.error('Select at least one note'); return; }
    setAiExtracting(true);
    try {
      const r = await axios.post(`${API}/ontology/ai-extract`, {
        note_ids: selectedNoteIds,
        target_kinds: ['class', 'relation', 'individual'],
        max_suggestions: 15,
      }, { headers });
      toast.success(`AI proposed ${r.data.created_count} elements`);
      setAiExtractOpen(false);
      setSelectedNoteIds([]);
      await fetchSuggestions();
      setTab('ai-capture');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI extraction failed'); }
    finally { setAiExtracting(false); }
  };

  const approveSuggestion = async (s) => {
    try {
      await axios.post(`${API}/ontology/suggestions/${s.suggestion_id}/approve`, {}, { headers });
      toast.success(`Approved: ${s.payload?.name}`);
      fetchSuggestions(); fetchClasses(); fetchRelations(); fetchHealth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const rejectSuggestion = async (s) => {
    try {
      await axios.post(`${API}/ontology/suggestions/${s.suggestion_id}/reject`, {}, { headers });
      toast.info(`Rejected: ${s.payload?.name}`);
      fetchSuggestions();
    } catch { toast.error('Reject failed'); }
  };

  const downloadJsonLd = async () => {
    try {
      const r = await axios.get(`${API}/ontology/export.jsonld`, { headers });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/ld+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kstreak-ontology-${new Date().toISOString().slice(0, 10)}.jsonld`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Ontology exported as JSON-LD');
    } catch { toast.error('Export failed'); }
  };

  const openAIDialog = () => { fetchNotesForAI(); setAiExtractOpen(true); };

  const runSemanticSearch = async () => {
    const q = searchQuery.trim();
    if (!q) { toast.error('Enter a question first'); return; }
    setSearchRunning(true);
    try {
      const r = await axios.post(`${API}/ontology/semantic-search`, { query: q, max_results: 30 }, { headers });
      setSearchResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Search failed'); }
    finally { setSearchRunning(false); }
  };

  const openBrief = async () => {
    if (!selectedClass) return;
    setBriefOpen(true);
    setBriefLoading(true);
    setBriefData(null);
    try {
      // Try latest first; 404 → auto-generate
      try {
        const r = await axios.get(`${API}/ontology/classes/${selectedClass.class_id}/brief/latest`, { headers });
        setBriefData(r.data);
      } catch (err) {
        if (err.response?.status === 404) {
          const r = await axios.post(`${API}/ontology/classes/${selectedClass.class_id}/brief`, {}, { headers });
          setBriefData(r.data);
        } else { throw err; }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Brief generation failed'); }
    finally { setBriefLoading(false); }
  };

  const regenerateBrief = async () => {
    if (!selectedClass) return;
    setBriefLoading(true);
    try {
      const r = await axios.post(`${API}/ontology/classes/${selectedClass.class_id}/brief`, {}, { headers });
      setBriefData(r.data);
      toast.success('Brief regenerated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Regeneration failed'); }
    finally { setBriefLoading(false); }
  };

  const downloadBriefPdf = async () => {
    if (!selectedClass) return;
    try {
      const r = await axios.get(`${API}/ontology/classes/${selectedClass.class_id}/brief.pdf`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kstreak-brief-${selectedClass.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Brief PDF downloaded');
    } catch { toast.error('PDF download failed'); }
  };

  const saveAuditConfig = async () => {
    if (!auditConfig) return;
    setAuditSaving(true);
    try {
      await axios.put(`${API}/ontology/audit-alert/config`, auditConfig, { headers });
      toast.success('Audit schedule saved');
      fetchAuditConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setAuditSaving(false); }
  };

  const runAuditNow = async () => {
    setAuditRunning(true);
    try {
      const r = await axios.post(`${API}/ontology/audit-alert/run-now`, {}, { headers });
      setAuditResult(r.data);
      toast.success(`Audit complete — ${r.data.flagged_count} flagged, ${r.data.brief_records?.length || 0} briefs`);
      fetchAuditConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Audit run failed'); }
    finally { setAuditRunning(false); }
  };

  const runDiff = async () => {
    if (!diffFrom || !diffTo) { toast.error('Pick both from and to'); return; }
    setDiffRunning(true);
    try {
      const body = {
        from_ts: new Date(diffFrom).toISOString(),
        to_ts: new Date(diffTo).toISOString(),
        include_commentary: true,
      };
      if (diffClassId) body.class_id = diffClassId;
      const r = await axios.post(`${API}/ontology/diff`, body, { headers });
      setDiffResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Diff failed'); }
    finally { setDiffRunning(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-6" data-testid="ontology-page">
      <div className="max-w-[1400px] mx-auto space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
              <GitBranch className="w-6 h-6 text-[#002FA7]" />
              Ontology
            </h1>
            <p className="text-sm text-[#6B7280] mt-1">
              Formal knowledge schema — classes, relations, and individuals that give structure to every note, article, and assessment.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadJsonLd} data-testid="export-jsonld-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export JSON-LD
            </Button>
            <Button size="sm" className="bg-[#002FA7] hover:bg-[#001F6F]" onClick={openAIDialog} data-testid="ai-extract-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> AI Capture from Notes
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-8 w-full max-w-5xl" data-testid="ontology-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview"><Activity className="w-3.5 h-3.5 mr-1" />Overview</TabsTrigger>
            <TabsTrigger value="classes" data-testid="tab-classes"><FolderTree className="w-3.5 h-3.5 mr-1" />Classes</TabsTrigger>
            <TabsTrigger value="relations" data-testid="tab-relations"><Link2 className="w-3.5 h-3.5 mr-1" />Relations</TabsTrigger>
            <TabsTrigger value="individuals" data-testid="tab-individuals"><Layers className="w-3.5 h-3.5 mr-1" />Individuals</TabsTrigger>
            <TabsTrigger value="graph" data-testid="tab-graph"><Network className="w-3.5 h-3.5 mr-1" />Graph</TabsTrigger>
            <TabsTrigger value="search" data-testid="tab-search"><Search className="w-3.5 h-3.5 mr-1" />Search</TabsTrigger>
            <TabsTrigger value="diff" data-testid="tab-diff"><Clock className="w-3.5 h-3.5 mr-1" />Diff</TabsTrigger>
            <TabsTrigger value="ai-capture" data-testid="tab-ai-capture">
              <Brain className="w-3.5 h-3.5 mr-1" />AI Queue
              {suggestions.length > 0 && <Badge variant="secondary" className="ml-1 text-[9px]">{suggestions.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="overview-kpis">
              <Card className="border-[#E5E7EB]">
                <CardContent className="p-4">
                  <p className="text-[10px] font-medium text-[#9CA3AF] uppercase tracking-wide">Health Score</p>
                  <p className="text-3xl font-bold mt-1" style={{ color: healthColor(health?.health_score) }} data-testid="kpi-health">{health ? health.health_score : '–'}</p>
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Coverage + Linkage + Freshness</p>
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB]"><CardContent className="p-4"><p className="text-[10px] font-medium text-[#9CA3AF] uppercase">Classes</p><p className="text-3xl font-bold text-[#002FA7] mt-1" data-testid="kpi-classes">{health?.totals?.classes ?? 0}</p></CardContent></Card>
              <Card className="border-[#E5E7EB]"><CardContent className="p-4"><p className="text-[10px] font-medium text-[#9CA3AF] uppercase">Relations</p><p className="text-3xl font-bold text-[#AF52DE] mt-1" data-testid="kpi-relations">{health?.totals?.relations ?? 0}</p></CardContent></Card>
              <Card className="border-[#E5E7EB]"><CardContent className="p-4"><p className="text-[10px] font-medium text-[#9CA3AF] uppercase">Individuals</p><p className="text-3xl font-bold text-[#34C759] mt-1" data-testid="kpi-individuals">{health?.totals?.individuals ?? 0}</p></CardContent></Card>
              <Card className="border-[#E5E7EB]"><CardContent className="p-4"><p className="text-[10px] font-medium text-[#9CA3AF] uppercase">Triples</p><p className="text-3xl font-bold text-[#FF9500] mt-1" data-testid="kpi-triples">{health?.totals?.triples ?? 0}</p></CardContent></Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-[#002FA7]" />Coverage</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#002FA7]">{health?.coverage?.coverage_pct ?? 0}%</span>
                    <span className="text-xs text-[#9CA3AF]">of classes have ≥1 individual</span>
                  </div>
                  <div className="bg-[#F3F4F6] rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-[#002FA7]" style={{ width: `${health?.coverage?.coverage_pct || 0}%` }} />
                  </div>
                  {health?.coverage?.leaf_no_individuals?.length > 0 && (
                    <div className="pt-2">
                      <p className="text-[11px] font-medium text-[#4B5563] mb-1">Classes with no instances:</p>
                      <div className="flex flex-wrap gap-1">
                        {health.coverage.leaf_no_individuals.slice(0, 8).map(c => (
                          <Badge key={c.class_id} variant="secondary" className="text-[10px]">{c.name}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF9500]" />Gaps & Orphans</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-xs">
                  <div className="flex justify-between"><span>Orphan classes</span><Badge variant="secondary">{health?.orphans?.count ?? 0}</Badge></div>
                  <div className="flex justify-between"><span>Floating individuals</span><Badge variant="secondary">{health?.floating_individuals ?? 0}</Badge></div>
                  <div className="flex justify-between"><span>Stale classes (&gt;60d)</span><Badge variant="secondary">{health?.stale?.classes ?? 0}</Badge></div>
                  <div className="flex justify-between"><span>Stale individuals</span><Badge variant="secondary">{health?.stale?.individuals ?? 0}</Badge></div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2"><History className="w-4 h-4 text-[#4B5563]" />Recent Activity</span>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setTab('audit')}>View full log →</Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RecentActivityList headers={headers} />
              </CardContent>
            </Card>

            <Card className="border-[#E5E7EB]" data-testid="audit-alert-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#FF9500]" />Weekly Audit Alert
                  {auditConfig?.enabled && <Badge className="text-[9px] bg-[#34C759]/10 text-[#34C759]">active</Badge>}
                </CardTitle>
                <p className="text-[11px] text-[#6B7280] mt-1">
                  Cron scans for classes with high growth or mutation activity, auto-generates Knowledge Briefs, and delivers a digest via email + Slack.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {!auditConfig && <p className="text-xs text-[#9CA3AF]">Loading…</p>}
                {auditConfig && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs cursor-pointer flex items-center gap-2">
                        <input type="checkbox" checked={!!auditConfig.enabled} onChange={(e) => setAuditConfig({ ...auditConfig, enabled: e.target.checked })} data-testid="audit-enabled-toggle" />
                        Enable weekly cron
                      </Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-[10px] text-[#9CA3AF]">Day</Label>
                          <Select value={auditConfig.day_of_week} onValueChange={(v) => setAuditConfig({ ...auditConfig, day_of_week: v })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['mon','tue','wed','thu','fri','sat','sun'].map(d => <SelectItem key={d} value={d}>{d.toUpperCase()}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-[#9CA3AF]">Hour (UTC)</Label>
                          <Input className="h-8 text-xs" type="number" min="0" max="23" value={auditConfig.hour} onChange={(e) => setAuditConfig({ ...auditConfig, hour: parseInt(e.target.value) || 0 })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-[10px] text-[#9CA3AF]">Growth threshold (%)</Label>
                          <Input className="h-8 text-xs" type="number" step="5" value={auditConfig.growth_threshold_pct} onChange={(e) => setAuditConfig({ ...auditConfig, growth_threshold_pct: parseFloat(e.target.value) || 0 })} data-testid="audit-growth-threshold" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-[#9CA3AF]">Min mutations/week</Label>
                          <Input className="h-8 text-xs" type="number" min="1" value={auditConfig.mutations_threshold} onChange={(e) => setAuditConfig({ ...auditConfig, mutations_threshold: parseInt(e.target.value) || 1 })} data-testid="audit-mutations-threshold" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-[#9CA3AF]">Email recipient</Label>
                      <Input className="h-8 text-xs" type="email" placeholder="cko@company.com" value={auditConfig.recipient_email} onChange={(e) => setAuditConfig({ ...auditConfig, recipient_email: e.target.value })} data-testid="audit-email-input" />
                      <Label className="text-[10px] text-[#9CA3AF] mt-2 block">Slack webhook (optional)</Label>
                      <Input className="h-8 text-xs" placeholder="https://hooks.slack.com/..." value={auditConfig.slack_webhook_url} onChange={(e) => setAuditConfig({ ...auditConfig, slack_webhook_url: e.target.value })} data-testid="audit-slack-input" />
                      {auditConfig.last_run_at && (
                        <p className="text-[10px] text-[#9CA3AF] mt-2">
                          Last run: {new Date(auditConfig.last_run_at).toLocaleString()} · flagged {auditConfig.last_result?.flagged_count ?? 0} · briefs {auditConfig.last_result?.briefs_generated ?? 0}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-xs bg-[#002FA7] hover:bg-[#001F6F]" onClick={saveAuditConfig} disabled={auditSaving || !auditConfig} data-testid="audit-save-btn">
                    {auditSaving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save schedule
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={runAuditNow} disabled={auditRunning} data-testid="audit-run-now-btn">
                    {auditRunning ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Running…</> : <><Sparkles className="w-3.5 h-3.5 mr-1" />Run now</>}
                  </Button>
                </div>
                {auditResult && (
                  <div className="p-2.5 rounded bg-[#F0F9FF] border border-[#BAE6FD] text-xs" data-testid="audit-result">
                    <p className="font-semibold text-[#0369A1] mb-1">Last run · {auditResult.flagged_count} flagged · {auditResult.brief_records?.length || 0} briefs generated</p>
                    <div className="flex flex-wrap gap-1">
                      {(auditResult.flagged || []).slice(0, 6).map(f => (
                        <Badge key={f.class_id} variant="secondary" className="text-[9px]">{f.class_name} · {f.triggers.join(', ')}</Badge>
                      ))}
                    </div>
                    {!auditResult.alerted_email && !auditResult.alerted_slack && (
                      <p className="text-[10px] text-[#9CA3AF] mt-1.5">No delivery channels configured — set an email or Slack webhook to receive digests.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#007AFF]" />Getting Started</CardTitle></CardHeader>
              <CardContent className="text-xs text-[#4B5563] space-y-2">
                <p><strong>1.</strong> Define <strong>Classes</strong> — the main concepts in your domain. Use parent/child to build a taxonomy.</p>
                <p><strong>2.</strong> Add <strong>Relations</strong> between classes with domain → range and cardinality.</p>
                <p><strong>3.</strong> Capture <strong>Individuals</strong> — concrete instances linked to PKM notes, articles, users.</p>
                <p><strong>4.</strong> Assert <strong>Triples</strong> between individuals to formalize knowledge as a queryable graph.</p>
                <p><strong>5.</strong> Run <strong>AI Capture</strong> to let GPT-5.2 propose ontology elements grounded in your notes.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="classes" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(340px,420px),1fr] gap-3">
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2 flex-row items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><FolderTree className="w-4 h-4 text-[#002FA7]" />Class Hierarchy ({classTree.total})</CardTitle>
                  <Button size="sm" className="h-7 text-xs bg-[#002FA7] hover:bg-[#001F6F]" onClick={() => setClassDialog({ mode: 'create', data: {}, parent: null })} data-testid="create-class-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />New Class
                  </Button>
                </CardHeader>
                <CardContent className="max-h-[70vh] overflow-y-auto" data-testid="class-tree">
                  {loading && <div className="flex items-center justify-center py-10 text-[#9CA3AF]"><Loader2 className="w-5 h-5 animate-spin" /></div>}
                  {!loading && classTree.roots.length === 0 && (
                    <div className="text-center py-10 text-xs text-[#9CA3AF]">
                      <Box className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      No classes yet. Create your first class to bootstrap the ontology.
                    </div>
                  )}
                  {!loading && classTree.roots.length > 0 && (
                    <ClassTreeNode
                      roots={classTree.roots}
                      onSelect={setSelectedClass}
                      onEdit={(n) => setClassDialog({ mode: 'edit', data: n, parent: null })}
                      onDelete={deleteClass}
                      onAddChild={(n) => setClassDialog({ mode: 'create', data: {}, parent: n })}
                      selectedId={selectedClass?.class_id}
                      expanded={expanded}
                      setExpanded={setExpanded}
                    />
                  )}
                </CardContent>
              </Card>

              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Class Detail</CardTitle></CardHeader>
                <CardContent data-testid="class-detail-panel">
                  {!selectedClass && <div className="text-center py-12 text-xs text-[#9CA3AF]">Select a class to see its description, properties, and individuals.</div>}
                  {selectedClass && (
                    <ClassDetail
                      classId={selectedClass.class_id}
                      headers={headers}
                      onEdit={() => setClassDialog({ mode: 'edit', data: selectedClass, parent: null })}
                      onAddIndividual={() => setIndividualDialog({ mode: 'create', data: { class_id: selectedClass.class_id } })}
                      onGenerateBrief={openBrief}
                      refreshKey={`${classes.length}-${individuals.length}`}
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="relations" className="mt-4">
            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2 flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><Link2 className="w-4 h-4 text-[#AF52DE]" />Relations ({relations.length})</CardTitle>
                <Button size="sm" className="h-7 text-xs bg-[#002FA7] hover:bg-[#001F6F]" onClick={() => setRelationDialog({ mode: 'create', data: {} })} data-testid="create-relation-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />New Relation
                </Button>
              </CardHeader>
              <CardContent>
                {relations.length === 0 && (
                  <div className="text-center py-12 text-xs text-[#9CA3AF]">
                    <Link2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    No relations defined yet.
                  </div>
                )}
                {relations.length > 0 && (
                  <div className="space-y-2" data-testid="relation-list">
                    {relations.map(r => {
                      const dom = (r.domain_class_ids || []).map(id => classes.find(c => c.class_id === id)?.name).filter(Boolean);
                      const rng = (r.range_class_ids || []).map(id => classes.find(c => c.class_id === id)?.name).filter(Boolean);
                      return (
                        <div key={r.relation_id} className="p-3 rounded-lg border border-[#E5E7EB] hover:bg-[#F9FAFB] group" data-testid={`relation-${r.relation_id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Badge className="text-[10px] bg-[#AF52DE]/10 text-[#AF52DE]">{r.name}</Badge>
                                {r.inverse_name && <Badge variant="secondary" className="text-[10px]">inverse: {r.inverse_name}</Badge>}
                                <Badge variant="secondary" className="text-[9px]">{r.cardinality}</Badge>
                              </div>
                              {r.description && <p className="text-xs text-[#4B5563] mt-1">{r.description}</p>}
                              <div className="flex items-center gap-1.5 mt-2 text-[11px] text-[#6B7280]">
                                <span className="font-mono">{dom.length > 0 ? dom.join(', ') : 'any'}</span>
                                <ArrowRight className="w-3 h-3" />
                                <span className="font-mono">{rng.length > 0 ? rng.join(', ') : 'any'}</span>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setRelationDialog({ mode: 'edit', data: r })}><Edit className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#FF3B30]" onClick={() => deleteRelation(r)}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="individuals" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(360px,440px),1fr] gap-3">
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4 text-[#34C759]" />Individuals</CardTitle>
                    <Button size="sm" className="h-7 text-xs bg-[#002FA7] hover:bg-[#001F6F]" onClick={() => setIndividualDialog({ mode: 'create', data: {} })} data-testid="create-individual-btn">
                      <Plus className="w-3.5 h-3.5 mr-1" />New Individual
                    </Button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Select value={classFilter || 'all'} onValueChange={(v) => setClassFilter(v === 'all' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid="individual-class-filter"><SelectValue placeholder="Filter by class" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All classes</SelectItem>
                        {classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="h-8 text-xs" placeholder="Search…" value={individualSearch} onChange={(e) => setIndividualSearch(e.target.value)} data-testid="individual-search" />
                  </div>
                </CardHeader>
                <CardContent className="max-h-[70vh] overflow-y-auto space-y-1" data-testid="individual-list">
                  {individuals.length === 0 && <div className="text-center py-10 text-xs text-[#9CA3AF]">No individuals match.</div>}
                  {individuals.map(i => {
                    const selCls = selectedIndividual?.individual_id === i.individual_id ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-[#E5E7EB]';
                    return (
                      <div
                        key={i.individual_id}
                        className={`p-2 rounded border cursor-pointer hover:border-[#002FA7] text-xs ${selCls}`}
                        onClick={() => openIndividualDetail(i)}
                        data-testid={`individual-${i.individual_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: i.class_color || '#34C759' }} />
                          <span className="font-medium text-[#111827] truncate">{i.name}</span>
                          <Badge variant="secondary" className="ml-auto text-[9px]">{i.class_name}</Badge>
                        </div>
                        {i.description && <p className="text-[10px] text-[#6B7280] mt-1 line-clamp-2">{i.description}</p>}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Individual Detail</CardTitle></CardHeader>
                <CardContent data-testid="individual-detail-panel">
                  {!individualDetail && <div className="text-center py-12 text-xs text-[#9CA3AF]">Select an individual to see details.</div>}
                  {individualDetail && (
                    <IndividualDetailView
                      detail={individualDetail}
                      onEdit={() => setIndividualDialog({ mode: 'edit', data: individualDetail })}
                      onDelete={() => deleteIndividual(individualDetail)}
                      onAddTriple={() => setTripleDialog({ subject_individual_id: individualDetail.individual_id })}
                      onDeleteTriple={deleteTriple}
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="graph" className="mt-4">
            <GraphTab
              graphData={graphData}
              graphLoading={graphLoading}
              graphIncludeIndivs={graphIncludeIndivs}
              setGraphIncludeIndivs={setGraphIncludeIndivs}
              onRefresh={fetchGraph}
            />
          </TabsContent>

          <TabsContent value="search" className="mt-4">
            <SemanticSearchTab
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchRunning={searchRunning}
              searchResult={searchResult}
              onRunSearch={runSemanticSearch}
              onOpenIndividual={(r) => { setTab('individuals'); openIndividualDetail(r); }}
            />
          </TabsContent>

          <TabsContent value="diff" className="mt-4">
            <DiffTab
              diffFrom={diffFrom}
              setDiffFrom={setDiffFrom}
              diffTo={diffTo}
              setDiffTo={setDiffTo}
              diffClassId={diffClassId}
              setDiffClassId={setDiffClassId}
              diffRunning={diffRunning}
              diffResult={diffResult}
              classes={classes}
              onRunDiff={runDiff}
            />
          </TabsContent>

          <TabsContent value="ai-capture" className="mt-4">
            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2"><Brain className="w-4 h-4 text-[#AF52DE]" />AI Suggestion Queue ({suggestions.length} pending)</span>
                  <Button size="sm" className="h-7 text-xs bg-[#002FA7] hover:bg-[#001F6F]" onClick={openAIDialog}>
                    <Sparkles className="w-3.5 h-3.5 mr-1" />Run extraction
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="suggestion-list">
                {suggestions.length === 0 && (
                  <div className="text-center py-12 text-xs text-[#9CA3AF]">
                    <Brain className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    No suggestions pending. Run AI extraction over PKM notes.
                  </div>
                )}
                {suggestions.map(s => {
                  const dnames = (s.payload?.domain_class_names || []).join(', ') || 'any';
                  const rnames = (s.payload?.range_class_names || []).join(', ') || 'any';
                  return (
                    <div key={s.suggestion_id} className="p-3 rounded-lg border border-[#E5E7EB]" data-testid={`suggestion-${s.suggestion_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[10px] ${kindBadgeClass(s.kind)}`}>{s.kind}</Badge>
                            <span className="font-semibold text-sm text-[#111827]">{s.payload?.name}</span>
                            {s.kind === 'individual' && s.payload?.class_name && <Badge variant="secondary" className="text-[9px]">of {s.payload.class_name}</Badge>}
                            {s.kind === 'class' && s.payload?.parent_class_name && <Badge variant="secondary" className="text-[9px]">under {s.payload.parent_class_name}</Badge>}
                          </div>
                          {s.payload?.description && <p className="text-xs text-[#4B5563] mt-1.5">{s.payload.description}</p>}
                          {s.rationale && <p className="text-[10px] text-[#9CA3AF] mt-1 italic">Rationale: {s.rationale}</p>}
                          {s.kind === 'relation' && (
                            <div className="text-[11px] text-[#6B7280] mt-1 font-mono">{dnames} → {rnames}</div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-7 text-xs text-[#34C759] border-[#34C759]/30" onClick={() => approveSuggestion(s)} data-testid={`approve-${s.suggestion_id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-[#FF3B30] border-[#FF3B30]/30" onClick={() => rejectSuggestion(s)} data-testid={`reject-${s.suggestion_id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4 text-[#4B5563]" />Change Log</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1 max-h-[70vh] overflow-y-auto">
                  {changeLog.length === 0 && <div className="text-center py-8 text-xs text-[#9CA3AF]">No changes logged yet.</div>}
                  {changeLog.map(c => (
                    <div key={c.log_id} className="flex items-center gap-2 p-2 rounded text-xs border border-[#E5E7EB]">
                      <Badge variant="secondary" className="text-[9px]">{c.action}</Badge>
                      <Badge className={`text-[9px] ${kindBadgeClass(c.entity_type)}`}>{c.entity_type}</Badge>
                      <span className="text-[#4B5563]">{c.summary}</span>
                      <span className="text-[#9CA3AF] ml-auto">{c.user_name} · {new Date(c.timestamp).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {classDialog && (
        <ClassFormDialog open={!!classDialog} initial={classDialog.data} parent={classDialog.parent} allClasses={classes} onCancel={() => setClassDialog(null)} onSubmit={submitClass} />
      )}
      {relationDialog && (
        <RelationFormDialog open={!!relationDialog} initial={relationDialog.data} classes={classes} onCancel={() => setRelationDialog(null)} onSubmit={submitRelation} />
      )}
      {individualDialog && (
        <IndividualFormDialog open={!!individualDialog} initial={individualDialog.data} classes={classes} headers={headers} onCancel={() => setIndividualDialog(null)} onSubmit={submitIndividual} />
      )}
      {tripleDialog && (
        <TripleFormDialog open={!!tripleDialog} subjectId={tripleDialog.subject_individual_id} relations={relations} headers={headers} onCancel={() => setTripleDialog(null)} onSubmit={submitTriple} />
      )}

      <Dialog open={briefOpen} onOpenChange={setBriefOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#AF52DE]" />
              Knowledge Brief {selectedClass && `— ${selectedClass.name}`}
            </DialogTitle>
            <DialogDescription>
              Executive brief authored by GPT-5.2, grounded in this class&apos;s taxonomy, individuals, linked PKM notes, and recent change-log activity.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto" data-testid="brief-content">
            {briefLoading && (
              <div className="flex flex-col items-center justify-center py-12 text-[#9CA3AF]">
                <Loader2 className="w-8 h-8 animate-spin mb-3" />
                <p className="text-sm">Authoring brief with GPT-5.2…</p>
              </div>
            )}
            {!briefLoading && briefData && (
              <div className="space-y-3">
                <div className="grid grid-cols-5 gap-2 text-center">
                  <div className="p-2 rounded bg-[#F3F4F6]"><p className="text-[9px] text-[#6B7280] uppercase">Children</p><p className="text-sm font-bold">{briefData.stats?.children ?? 0}</p></div>
                  <div className="p-2 rounded bg-[#F3F4F6]"><p className="text-[9px] text-[#6B7280] uppercase">Individuals</p><p className="text-sm font-bold">{briefData.stats?.individuals ?? 0}</p></div>
                  <div className="p-2 rounded bg-[#F3F4F6]"><p className="text-[9px] text-[#6B7280] uppercase">Relations</p><p className="text-sm font-bold">{briefData.stats?.relations ?? 0}</p></div>
                  <div className="p-2 rounded bg-[#F3F4F6]"><p className="text-[9px] text-[#6B7280] uppercase">Linked Notes</p><p className="text-sm font-bold">{briefData.stats?.linked_notes ?? 0}</p></div>
                  <div className="p-2 rounded bg-[#F3F4F6]"><p className="text-[9px] text-[#6B7280] uppercase">Recent Activity</p><p className="text-sm font-bold">{briefData.stats?.recent_log_entries ?? 0}</p></div>
                </div>
                <MarkdownBrief markdown={briefData.markdown || ''} />
                <p className="text-[9px] text-[#9CA3AF] pt-2 border-t border-[#F3F4F6]">
                  Generated {new Date(briefData.generated_at).toLocaleString()} by {briefData.generated_by_name}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={regenerateBrief} disabled={briefLoading}>
              <Sparkles className="w-4 h-4 mr-1.5" />Regenerate
            </Button>
            <Button className="bg-[#002FA7] hover:bg-[#001F6F]" onClick={downloadBriefPdf} disabled={briefLoading || !briefData} data-testid="download-brief-pdf">
              <FileDown className="w-4 h-4 mr-1.5" />Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={aiExtractOpen} onOpenChange={setAiExtractOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#AF52DE]" />AI-assisted Ontology Capture</DialogTitle>
            <DialogDescription>
              Select PKM notes. GPT-5.2 analyses them and proposes classes, relations, and individuals — every proposal enters the approval queue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Selected: {selectedNoteIds.length} notes</Label>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setSelectedNoteIds(availableNotes.map(n => n.note_id))}>Select all</Button>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setSelectedNoteIds([])}>Clear</Button>
              </div>
            </div>
            <div className="max-h-[340px] overflow-y-auto border border-[#E5E7EB] rounded-lg divide-y" data-testid="ai-note-selector">
              {availableNotes.length === 0 && <div className="text-center py-6 text-xs text-[#9CA3AF]">No PKM notes found.</div>}
              {availableNotes.map(n => {
                const checked = selectedNoteIds.includes(n.note_id);
                const rowCls = checked ? 'bg-[#002FA7]/5' : '';
                return (
                  <label key={n.note_id} className={`flex items-start gap-2 p-2 cursor-pointer hover:bg-[#F9FAFB] ${rowCls}`} data-testid={`ai-note-${n.note_id}`}>
                    <input type="checkbox" checked={checked} onChange={(e) => {
                      setSelectedNoteIds(e.target.checked ? [...selectedNoteIds, n.note_id] : selectedNoteIds.filter(x => x !== n.note_id));
                    }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-[#111827] truncate">{n.title}</div>
                      <div className="text-[10px] text-[#9CA3AF] line-clamp-1">{(n.content || '').slice(0, 120)}</div>
                    </div>
                    <Badge variant="secondary" className="text-[9px]">{n.note_type}</Badge>
                  </label>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiExtractOpen(false)} disabled={aiExtracting}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#001F6F]" onClick={runAIExtract} disabled={aiExtracting || selectedNoteIds.length === 0} data-testid="run-ai-extract">
              {aiExtracting && <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Extracting…</>}
              {!aiExtracting && <><Sparkles className="w-4 h-4 mr-1.5" />Extract</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OntologyPage;
